import { useState, useEffect, useCallback } from 'react';

// hook for backend status checking


const useBackendStatus = (checkUrl = import.meta.env.VITE_API_URL, retryInterval = 30000) => {
  const [isOnline, setIsOnline] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const checkBackendHealth = useCallback(async () => {
    setIsChecking(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

      const response = await fetch(checkUrl, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        setIsOnline(true);
        setRetryCount(0);
      } else {
        setIsOnline(false);
        setRetryCount(prev => prev + 1);
      }
    } catch (error) {
      // Network error, server crash, timeout, etc.
      setIsOnline(false);
      setRetryCount(prev => prev + 1);
    } finally {
      setIsChecking(false);
      setLastChecked(new Date());
    }
  }, [checkUrl]);

  const manualRetry = useCallback(() => {
    if (!isChecking) {
    checkBackendHealth();
    }
  }, [checkBackendHealth, isChecking]);

  useEffect(() => {
    // Initial check
    checkBackendHealth();

    // Set up interval for auto-retry
    const interval = setInterval(checkBackendHealth, retryInterval);

    return () => clearInterval(interval);
  }, [checkBackendHealth, retryInterval]);

  return {
    isOnline,
    isChecking,
    lastChecked,
    retryCount,
    manualRetry
  };
};

export default useBackendStatus;